import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Copy, Download, ExternalLink, Loader2, RefreshCw, Upload } from 'lucide-react';
import { API } from '../../config/apiBase';
import AffiliateBuffer from './AffiliateBuffer';
import {
    ETIQUETA_PADRAO, LOTE_PADRAO, PASSOS, PORTAL_URL, ROTA_API, STATUS_PREVIEW, TAMANHOS_LOTE, TIERS, contagemPreview,
    alertasCobertura, coberturaElegiveis, dataCurta, idadeSnapshot, importaveis, nomeArquivo, pct, podeImportar, simNao,
} from './mlAfiliados';

/**
 * /sistema/ml-afiliados — Mercado Livre, cobertura de afiliados (workflow MANUAL oficial).
 *
 * Somente admin (a API também exige). Não existe API pública do ML para gerar link de afiliado:
 * a página organiza o trabalho no Portal — lote priorizado, exportação, preview e import explícito.
 * Não abre produto, não gera link, não altera publicação; o hard gate exige afiliado válido.
 */
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');

async function chamar(caminho, opcoes = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);
    try {
    const r = await fetch(`${API}${ROTA_API}${caminho}`, {
        ...opcoes,
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
    });
    let corpo = null;
    try { corpo = await r.json(); } catch (_) { corpo = null; }
    if (!r.ok) throw new Error((corpo && corpo.detail) || `HTTP ${r.status}`);
    return corpo;
    } catch (e) {
        if (e.name === 'AbortError') throw new Error('Tempo limite atingido. Confira o preview antes de tentar novamente; a operação pode ter sido concluída no servidor.');
        throw e;
    } finally { clearTimeout(timeout); }
}

function baixar(nome, conteudo, tipo) {
    const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const suave = { color: 'var(--muted)' };
const campo = { background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' };

function Bloco({ titulo, dados, testid, extra }) {
    return (
        <div className="rounded-lg px-3 py-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={testid}>
            <p className="text-[11px] font-semibold" style={suave}>{titulo}</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{pct(dados && dados.coverage_percent)}</p>
            <p className="text-[11px]" style={suave}>
                {(dados && dados.total) || 0} no total · {(dados && dados.with_affiliate) || 0} com · {(dados && dados.without_affiliate) || 0} sem
            </p>
            {extra ? <p className="text-[11px]" style={suave}>{extra}</p> : null}
        </div>
    );
}

export default function MlAfiliadosPage() {
    const [resumo, setResumo] = useState(null);
    const [erro, setErro] = useState('');
    const [top, setTop] = useState(LOTE_PADRAO);
    const [lote, setLote] = useState(null);
    const [ocupado, setOcupado] = useState('');
    const [texto, setTexto] = useState('');
    const [etiqueta, setEtiqueta] = useState(ETIQUETA_PADRAO);
    const [preview, setPreview] = useState(null);
    const [importado, setImportado] = useState(null);
    const [aviso, setAviso] = useState('');
    const [confirmarImport, setConfirmarImport] = useState(false);
    const importEmCurso = useRef(false);

    const carregar = useCallback(async () => {
        setErro('');
        try { setResumo(await chamar('/summary')); } catch (e) { setErro(e.message); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const gerarLote = async () => {
        setOcupado('lote'); setAviso('');
        try { setLote(await chamar('/lote', { method: 'POST', body: JSON.stringify({ top }) })); } catch (e) { setAviso(e.message); }
        setOcupado('');
    };

    const copiar = async (conteudo) => {
        try { await navigator.clipboard.writeText(conteudo); setAviso('URLs copiadas.'); } catch (_) { setAviso('Não foi possível copiar; use Baixar TXT.'); }
    };

    const lerArquivo = (ev) => {
        const f = ev.target.files && ev.target.files[0];
        if (!f) return;
        if (f.size > 200000) { setAviso('Arquivo acima de 200 KB; divida o lote.'); return; }
        const r = new FileReader();
        r.onload = () => { setTexto(String(r.result || '')); setPreview(null); setImportado(null); };
        r.readAsText(f);
    };

    const fazerPreview = async () => {
        setOcupado('preview'); setAviso(''); setImportado(null);
        try { setPreview(await chamar('/preview', { method: 'POST', body: JSON.stringify({ texto, etiqueta, generation_reason: 'eligible_missing' }) })); } catch (e) { setAviso(e.message); setPreview(null); }
        setOcupado('');
    };

    const importar = async () => {
        if (importEmCurso.current || !podeImportar(preview, importado)) return;
        importEmCurso.current = true;
        setOcupado('import'); setAviso('');
        try {
            setImportado(await chamar('/import', { method: 'POST', body: JSON.stringify({ preview_id: preview.preview_id }) }));
            setConfirmarImport(false);
            await carregar();
        } catch (e) { setAviso(e.message); }
        finally { importEmCurso.current = false; setOcupado(''); }
    };

    if (!resumo && !erro) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

    return (
        <div className="space-y-4" data-testid="ml-afiliados-page">
            {confirmarImport ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div role="dialog" aria-modal="true" aria-labelledby="ml-import-title" className="sv-card-system max-w-lg space-y-4" style={{ background: 'var(--surface)', color: 'var(--text)' }}>
                    <h2 id="ml-import-title">Importar {importaveis(preview)} links válidos?</h2>
                    <p>Inválidos, duplicados, conflitos e sem correspondência ficam de fora.</p>
                    {aviso ? <p role="alert">{aviso}</p> : null}
                    <button type="button" className="sv-btn-system" disabled={ocupado === 'import'} onClick={() => setConfirmarImport(false)}>Cancelar</button>
                    <button type="button" autoFocus className="sv-btn-gold-system sv-btn-system" data-testid="ml-affiliate-import-confirm" disabled={ocupado === 'import'} onClick={importar}>
                        {ocupado === 'import' ? 'Importando...' : 'Confirmar importação'}
                    </button>
                </div>
            </div> : null}
            {resumo ? <AffiliateBuffer buffer={resumo.affiliate_buffer}
                eligibleMissing={coberturaElegiveis(resumo.eligible).missing}
                refused={(resumo.portal_refused || []).length} /> : null}
            {erro ? <div className="sv-card-system text-sm" style={{ color: 'var(--status-red)' }}>Erro: {erro}</div> : null}
            {resumo ? alertasCobertura(coberturaElegiveis(resumo.eligible)).map((a) => (
                <div key={a.texto} className="sv-card-system flex items-start gap-2 text-sm" data-testid={`ml-alerta-${a.nivel}`}
                     style={{ borderColor: a.nivel === 'forte' ? 'var(--status-error-border)' : 'var(--status-warning-border)' }}>
                    <AlertTriangle size={16} style={{ color: a.nivel === 'forte' ? 'var(--status-red)' : 'var(--status-amber)' }} />
                    <div>
                        <b>{a.texto}</b>
                        <span style={suave}> {resumo.goal.require_affiliate_link
                            ? 'O hard gate já impede publicar oferta sem afiliado no WhatsApp; sem candidato monetizado o slot fica vazio.'
                            : 'Informativo.'}</span>
                    </div>
                </div>
            )) : null}

            {resumo ? (() => {
                const c = coberturaElegiveis(resumo.eligible);
                return (
                    <div className="sv-card-system" data-testid="ml-cobertura-ml">
                        <b style={{ color: 'var(--text)' }}>Cobertura ML (elegíveis WhatsApp)</b>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2 text-xs" style={{ color: 'var(--text)' }}>
                            <div>Official<br /><b className="text-lg">{c.official}</b></div>
                            <div>Recusados pelo Portal<br /><b className="text-lg">{c.refused}</b></div>
                            <div>Missing<br /><b className="text-lg" data-testid="ml-missing">{c.missing}</b></div>
                            <div>Cobertura bruta<br /><b className="text-lg">{pct(c.bruta)}</b></div>
                            <div>Cobertura operacional<br /><b className="text-lg" data-testid="ml-monetizavel">{pct(c.monetizavel)}</b></div>
                        </div>
                        <p className="text-[11px] mt-1" style={suave}>Operacional = official ÷ (official + missing): recusados pelo Portal ficam fora porque hoje não podem ser monetizados.</p>
                    </div>
                );
            })() : null}

            {resumo ? (
                <div className="sv-card-system">
                    <div className="flex items-center justify-between mb-3">
                        <b style={{ color: 'var(--text)' }}>Cobertura</b>
                        <button type="button" className="sv-btn-system text-xs flex items-center gap-1" onClick={carregar} data-testid="ml-recarregar">
                            <RefreshCw size={12} /> {idadeSnapshot(resumo.snapshot)}
                        </button>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        <Bloco titulo="Deals ML ativos" dados={resumo.global} testid="ml-cob-global" />
                        <Bloco titulo="Elegíveis WhatsApp" dados={resumo.eligible} testid="ml-cob-elegiveis" />
                        <Bloco titulo={`F1 de hoje${resumo.f1_today.day ? ` (${resumo.f1_today.day})` : ''}`} dados={resumo.f1_today} testid="ml-cob-f1" />
                        <Bloco titulo="Próximos candidatos (simulados)" dados={resumo.next_candidates} testid="ml-cob-proximos" />
                    </div>
                    <p className="text-[11px] mt-2" style={suave}>
                        Meta: {resumo.goal.links_per_day[0]}–{resumo.goal.links_per_day[1]} links oficiais por dia; elegíveis ≥ {resumo.goal.eligible_target_percent}%.
                        Exigir link de afiliado para publicar: {simNao(resumo.goal.require_affiliate_link)}.
                    </p>
                </div>
            ) : null}

            {resumo ? (
                <div className="sv-card-system overflow-x-auto">
                    <b style={{ color: 'var(--text)' }}>Por família (elegíveis WhatsApp)</b>
                    <table className="w-full text-xs mt-2" style={{ color: 'var(--text)' }} data-testid="ml-familias">
                        <thead><tr style={suave}><th className="text-left">Família</th><th>Elegíveis</th><th>Com</th><th>Sem</th><th>%</th></tr></thead>
                        <tbody>{Object.entries(resumo.families).map(([f, c]) => (
                            <tr key={f}><td>{f}</td><td className="text-center">{c.total}</td><td className="text-center">{c.with_affiliate}</td>
                                <td className="text-center">{c.without_affiliate}</td><td className="text-center">{pct(c.coverage_percent)}</td></tr>
                        ))}</tbody>
                    </table>
                </div>
            ) : null}

            <div className="sv-card-system">
                <b style={{ color: 'var(--text)' }}>Como gerar os links (oficial, manual)</b>
                <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm" style={{ color: 'var(--text)' }} data-testid="ml-passos">
                    {PASSOS.map((p, i) => <li key={i}><b>Passo {i + 1}.</b> {p}</li>)}
                </ol>
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs mt-2" style={{ color: 'var(--sv-gold-primary)' }}>
                    Programa de Afiliados do Mercado Livre <ExternalLink size={12} />
                </a>
                <p className="text-[11px] mt-1" style={suave}>Sem automação de login, cookies ou do Portal. Nenhum parâmetro de afiliado é montado aqui.</p>
            </div>

            <div className="sv-card-system space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <b style={{ color: 'var(--text)' }}>Lote para o Portal</b>
                    <select value={top} onChange={(e) => setTop(Number(e.target.value))} className="rounded px-2 py-1 text-sm" style={campo} data-testid="ml-top">
                        {TAMANHOS_LOTE.map((n) => <option key={n} value={n}>{`Top ${n}`}</option>)}
                    </select>
                    <button type="button" className="sv-btn-gold-system sv-btn-system text-sm" onClick={gerarLote} disabled={ocupado === 'lote'} data-testid="ml-gerar-lote">
                        {ocupado === 'lote' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar lote para Portal'}
                    </button>
                </div>
                {lote ? (
                    <div className="space-y-2" data-testid="ml-lote">
                        <div className="flex flex-wrap gap-2">
                            <button type="button" className="sv-btn-system text-xs flex items-center gap-1" onClick={() => copiar(lote.txt)}><Copy size={12} /> Copiar URLs</button>
                            <button type="button" className="sv-btn-system text-xs flex items-center gap-1" onClick={() => baixar(nomeArquivo(lote.lot_id, 'txt'), lote.txt, 'text/plain')} data-testid="ml-baixar-txt"><Download size={12} /> Baixar TXT</button>
                            <button type="button" className="sv-btn-system text-xs flex items-center gap-1" onClick={() => baixar(nomeArquivo(lote.lot_id, 'csv'), lote.csv, 'text/csv')} data-testid="ml-baixar-csv"><Download size={12} /> Baixar CSV (referência)</button>
                        </div>
                        <p className="text-[11px]" style={suave}>
                            {lote.total} produtos · {Object.entries(lote.por_tier).map(([k, n]) => `${TIERS[k] || k}: ${n}`).join(' · ')} · lote {lote.lot_id}
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]" style={{ color: 'var(--text)' }}>
                                <thead><tr style={suave}><th>#</th><th className="text-left">Prioridade</th><th className="text-left">MLB</th><th className="text-left">Título</th><th className="text-left">Categoria</th><th>Score</th><th className="text-left">URL canônica</th></tr></thead>
                                <tbody>{lote.itens.map((x) => (
                                    <tr key={x.mlb}><td>{x.priority}</td><td>{TIERS[x.tier] || x.tier}{x.f1_window ? ` · ${x.f1_window.split(':')[1]}` : ''}</td><td>{x.mlb}</td>
                                        <td>{x.title}</td><td>{x.category || '—'}</td><td className="text-center">{x.score === null || x.score === undefined ? '—' : Number(x.score).toFixed(1)}</td>
                                        <td className="break-all">{x.canonical_url}</td></tr>
                                ))}</tbody>
                            </table>
                        </div>
                    </div>
                ) : null}
            </div>

            <div className="sv-card-system space-y-2">
                <b style={{ color: 'var(--text)' }}>Importar resultado do Portal</b>
                <p className="text-[11px]" style={suave}>Cada linha precisa ter o produto (MLB ou URL /p/MLB) e o link oficial. A ordem das linhas não é usada para associar.</p>
                <textarea value={texto} onChange={(e) => { setTexto(e.target.value); setPreview(null); setImportado(null); }} rows={6}
                          className="w-full rounded px-2 py-1 text-xs font-mono" style={campo} placeholder="https://www.mercadolivre.com.br/p/MLB123  https://meli.la/AbC1234" data-testid="ml-texto" />
                <div className="flex flex-wrap items-center gap-2">
                    <label className="sv-btn-system text-xs flex items-center gap-1 cursor-pointer"><Upload size={12} /> Enviar CSV/TXT
                        <input type="file" accept=".csv,.txt,text/plain,text/csv" className="hidden" onChange={lerArquivo} data-testid="ml-arquivo" />
                    </label>
                    <label className="text-xs" style={suave}>Etiqueta usada no Portal
                        <input value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)} className="ml-1 rounded px-2 py-1 text-xs" style={campo} data-testid="ml-etiqueta" />
                    </label>
                    <button type="button" className="sv-btn-gold-system sv-btn-system text-sm" onClick={fazerPreview} disabled={!texto.trim() || ocupado === 'preview'} data-testid="ml-preview">
                        {ocupado === 'preview' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pré-visualizar'}
                    </button>
                </div>
                {preview ? (
                    <div className="space-y-2" data-testid="ml-preview-resultado">
                        <div className="flex flex-wrap gap-2 text-xs">
                            {contagemPreview(preview).map((c) => <span key={c.status} className={`sv-badge ${STATUS_PREVIEW[c.status].cls}`}>{c.label}: {c.n}</span>)}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]" style={{ color: 'var(--text)' }}>
                                <thead><tr style={suave}><th>Linha</th><th className="text-left">Status</th><th className="text-left">MLB</th><th className="text-left">Deal</th><th className="text-left">Link</th><th className="text-left">Identidade</th><th className="text-left">Motivo</th></tr></thead>
                                <tbody>{preview.linhas.filter((x) => !x.ignorada).map((x) => (
                                    <tr key={x.linha} data-testid="ml-preview-linha"><td>{x.linha}</td>
                                        <td><span className={`sv-badge ${(STATUS_PREVIEW[x.status] || {}).cls || 'sv-badge-muted'}`}>{(STATUS_PREVIEW[x.status] || {}).label || x.status}</span></td>
                                        <td>{x.mlb || '—'}</td><td>{x.deal_id || '—'}</td><td className="break-all">{x.affiliate_url || '—'}</td><td>{x.identity || '—'}</td><td>{x.motivo || ''}</td></tr>
                                ))}</tbody>
                            </table>
                        </div>
                        <button type="button" className="sv-btn-gold-system sv-btn-system text-sm" onClick={() => { setAviso(''); setConfirmarImport(true); }} disabled={!podeImportar(preview, importado) || ocupado === 'import'} data-testid="ml-importar">
                            {ocupado === 'import' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Importar links válidos (${importaveis(preview)})`}
                        </button>
                    </div>
                ) : null}
                {importado ? (
                    <p className="text-sm" style={{ color: 'var(--status-green)' }} data-testid="ml-importado">
                        Importados {importado.criados} link(s) · import {importado.import_id}{importado.recusados.length ? ` · ${importado.recusados.length} recusado(s) na revalidação` : ''}
                    </p>
                ) : null}
                {aviso ? <p className="text-xs" style={{ color: 'var(--status-amber)' }} data-testid="ml-aviso">{aviso}</p> : null}
            </div>

            {resumo ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="sv-card-system overflow-x-auto">
                        <b style={{ color: 'var(--text)' }}>F1 de hoje</b>
                        <table className="w-full text-[11px] mt-2" style={{ color: 'var(--text)' }} data-testid="ml-f1-hoje">
                            <thead><tr style={suave}><th className="text-left">Slot</th><th className="text-left">MLB</th><th className="text-left">Categoria</th><th>Afiliado</th><th className="text-left">Fonte</th></tr></thead>
                            <tbody>{resumo.f1_today.publicacoes.map((p) => (
                                <tr key={p.publication_id}><td>{String(p.window).split(':')[1]}</td><td>{p.mlb || '—'}</td><td>{p.category || '—'}</td>
                                    <td className="text-center">{simNao(p.official)}</td><td>{p.fonte}</td></tr>
                            ))}</tbody>
                        </table>
                    </div>
                    <div className="sv-card-system overflow-x-auto">
                        <b style={{ color: 'var(--text)' }}>Próximos slots (simulação)</b>
                        <table className="w-full text-[11px] mt-2" style={{ color: 'var(--text)' }} data-testid="ml-proximos">
                            <thead><tr style={suave}><th className="text-left">Slot</th><th className="text-left">MLB</th><th className="text-left">Categoria</th><th>Afiliado</th></tr></thead>
                            <tbody>{resumo.next_candidates.itens.map((p) => (
                                <tr key={p.window}><td>{p.window}</td><td>{p.mlb || '—'}</td><td>{p.category || '—'}</td><td className="text-center">{simNao(p.official)}</td></tr>
                            ))}</tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {resumo && (resumo.portal_refused || []).length > 0 ? (
                <div className="sv-card-system overflow-x-auto" data-testid="ml-recusados">
                    <b style={{ color: 'var(--text)' }}>Recusados pelo Portal</b>
                    <p className="text-[11px]" style={suave}>Não é conflito: o Programa não permite estes produtos. Ficam fora dos lotes até a próxima tentativa (manual).</p>
                    <table className="w-full text-[11px] mt-2" style={{ color: 'var(--text)' }}>
                        <thead><tr style={suave}><th className="text-left">MLB</th><th className="text-left">Categoria</th><th>1ª recusa</th><th>Última</th><th className="text-left">Motivo</th><th>Nova tentativa</th></tr></thead>
                        <tbody>{resumo.portal_refused.map((x) => (
                            <tr key={x.mlb}><td>{x.mlb}</td><td>{x.category || '—'}</td><td className="text-center">{dataCurta(x.first_refused_at)}</td>
                                <td className="text-center">{dataCurta(x.last_refused_at)}</td><td>{x.reason}</td>
                                <td className="text-center">{x.in_cooldown ? dataCurta(x.next_retry_at) : 'liberado'}</td></tr>
                        ))}</tbody>
                    </table>
                </div>
            ) : null}

            {resumo ? (
                <div className="sv-card-system text-xs" style={{ color: 'var(--text)' }} data-testid="ml-backfill">
                    <b>Backfill gradual (só manual via Portal)</b>
                    <ul className="mt-1 space-y-0.5">{resumo.backfill.map((f) => <li key={f.fase}>Fase {f.fase} — {f.descricao}: <b>{f.pendentes}</b> pendentes</li>)}</ul>
                </div>
            ) : null}
        </div>
    );
}
